// Shingan's fight effects (M10 Kanemori Sanctuary): the naginata wind-up telegraph and sweep, the shockwave ring, the
// blink afterimages, his arrival, the decoys' hologram shimmer and their pop. Presentation only: everything is read
// from the snapshot's `sg` (wu, bl, dc) and the shockwave's 'boom' event, and never decides a hit. The telegraph is
// drawn from the same numbers the server sweeps with (config sanctuary.boss.melee reach and arc, his facing frozen
// during the wind-up), so the warned area is the hit area. Live effects sit in fixed pools sized by city-art.json
// `shinganFx` (validated in data.ts); everything bright is scaled by the comfort grade; nothing covers the screen.
import { BOSS_FX } from './fx-flags';
import { Container, Graphics, Sprite, type Texture } from 'pixi.js';
import { CONFIG, CITY_ART } from '../../common/src/data';
import type { NpcSnap, Snapshot } from '../../common/src/protocol';
import { FxPool } from './fx-pool';
import { retainBodyTexture, releaseBodyTexture } from './character-art';
import { PALETTE, STATE } from './theme';
import { decoyShimmer, telegraphWedge } from './shingan-shapes';
export { decoyShimmer };

type Sg = NonNullable<Snapshot['sg']>;
const SF = CITY_ART.shinganFx;
const hex = (s: string) => parseInt(s.slice(1), 16);
const WARN = hex(STATE.warning), DANGER = hex(STATE.danger), WHITE = hex(PALETTE.offWhite), CYAN = hex(PALETTE.cyan), INK = hex(PALETTE.midnight);
/** Extra flourish (arrival embers, a second sweep echo) follows the boss-effects review flag; the warnings never do. */
export const SHINGAN_FX_REVIEW = BOSS_FX;
const B = () => CONFIG.sanctuary.boss;

type Ring = { x: number; y: number; r: number; t0: number; dur: number; col: number; w: number };
type Sweep = { x: number; y: number; a: number; t0: number };
type Pop = { x: number; y: number; t0: number };
type Bit = { x: number; y: number; vx: number; vy: number; t0: number; life: number; col: number };
type Ghost = { s: Sprite; t0: number; held: boolean };

export class ShinganFx {
  /** on the ground, under the people: the telegraph, rings */
  ground = new Graphics();
  /** over the people: sweeps, decoy pops and bits, the arrival pillar, shimmer bands */
  over = new Graphics();
  private rings = new FxPool<Ring>(SF.rings);
  private sweeps = new FxPool<Sweep>(2);
  private streaks = new FxPool<{ x: number; y: number; x1: number; y1: number; t0: number }>(2);
  private pops = new FxPool<Pop>(SF.pops);
  private bits = new FxPool<Bit>(SF.bits);
  private ghosts: Ghost[] = [];
  private arrive: { x: number; y: number; t0: number } | null = null;
  private prev: { id: number; wu: 0 | 1; bl: string; dc: Map<number, { x: number; y: number }> } | null = null;
  private seen = false;
  private wind: { t0: number; a: number } | null = null;
  constructor(ground: Container, over: Container, private chars: Container) { ground.addChild(this.ground); over.addChild(this.over); }

  /** Read this frame's `sg` and the interpolated NPCs. Returns true on the frame his naginata sweeps (the rig swings). */
  update(sg: Sg | undefined, npcs: NpcSnap[], t: number, body?: Texture, bodyScale = 1): boolean {
    const boss = sg && npcs.find(n => n.id === sg.id), prev = this.prev, first = !this.seen;
    this.seen = true;
    let swung = false;
    if (sg && boss) {
      // arrival: a new appearance (not the one already there when this player joined)
      if ((!prev || prev.id !== sg.id) && !first) this.arrival(boss.x, boss.y, t);
      if (sg.wu && (!prev || !prev.wu || prev.id !== sg.id)) this.wind = { t0: t, a: boss.a };
      if (!sg.wu && prev?.wu && prev.id === sg.id) { this.sweeps.put({ x: boss.x, y: boss.y, a: this.wind?.a ?? boss.a, t0: t }); swung = true; }
      if (!sg.wu) this.wind = null;
      const bl = sg.bl ? sg.bl.join(',') : '';
      if (bl && bl !== prev?.bl && sg.bl) this.blink(sg.bl[0], sg.bl[1], boss.x, boss.y, t, body, bodyScale);
    } else this.wind = null;
    // a decoy that left the snapshot while it was near him popped (hit, stripped, or he went down)
    const dc = new Map<number, { x: number; y: number }>();
    for (const id of sg?.dc ?? []) { const d = npcs.find(n => n.id === id); if (d) dc.set(id, { x: d.x, y: d.y }); }
    if (prev) for (const [id, p] of prev.dc) if (!dc.has(id) && (!sg || !sg.dc.includes(id))) this.pop(p.x, p.y, t);
    this.prev = sg && boss ? { id: sg.id, wu: sg.wu, bl: sg.bl ? sg.bl.join(',') : '', dc } : null;
    return swung;
  }
  /** the shockwave ('boom' 'elec' on him): a ring out to the server's radius */
  shock(x: number, y: number, t: number) {
    const R = B().shockwave.radius;
    this.rings.put({ x, y, r: R, t0: t, dur: SF.ringSec, col: WHITE, w: 3 });
    this.rings.put({ x, y, r: R * 0.92, t0: t, dur: SF.ringSec * 0.8, col: CYAN, w: 1 });
  }
  /** is (x, y) where he stands (the shockwave event is emitted at his feet) */
  isHim(sg: Sg | undefined, npcs: NpcSnap[], x: number, y: number) {
    const b = sg && npcs.find(n => n.id === sg.id); return !!b && Math.hypot(b.x - x, b.y - y) < 24;
  }
  private arrival(x: number, y: number, t: number) {
    this.arrive = { x, y, t0: t };
    this.rings.put({ x, y, r: 26, t0: t, dur: SF.arriveSec * 0.6, col: WHITE, w: 2 });
    if (SHINGAN_FX_REVIEW) for (let i = 0; i < 10; i++) this.bit(x + (Math.random() - 0.5) * 16, y, (Math.random() - 0.5) * 16, -30 - Math.random() * 30, t, SF.arriveSec, i % 3 ? WHITE : CYAN);
  }
  private blink(x0: number, y0: number, x1: number, y1: number, t: number, body?: Texture, scale = 1) {
    this.rings.put({ x: x0, y: y0, r: 14, t0: t, dur: SF.ghostSec, col: CYAN, w: 1 });
    this.rings.put({ x: x1, y: y1, r: 14, t0: t, dur: SF.ghostSec, col: WHITE, w: 1 });
    this.streaks.put({ x: x0, y: y0, x1, y1, t0: t });
    if (!body) return;
    const n = SF.ghosts;
    for (let i = 0; i < n; i++) { // afterimages from where he was toward where he is, fading in order
      const f = i / n; let g = this.ghosts[i];
      if (!g) { const s = new Sprite(); s.anchor.set(0.5, 1); this.chars.addChild(s); g = this.ghosts[i] = { s, t0: -9, held: false }; }
      if (g.s.texture !== body) { retainBodyTexture(body); if (g.held) releaseBodyTexture(g.s.texture); g.s.texture = body; g.held = true; } // held while shown: the body cache can't prune it
      g.s.scale.set(scale); g.s.tint = i % 2 ? CYAN : WHITE;
      g.s.position.set(x0 + (x1 - x0) * f, y0 + (y1 - y0) * f + 5); g.s.zIndex = g.s.y - 0.5; g.t0 = t - f * 0.12; g.s.visible = true;
    }
  }
  private pop(x: number, y: number, t: number) {
    this.pops.put({ x, y, t0: t });
    for (let i = 0; i < 8; i++) { const a = i * Math.PI / 4 + Math.random() * 0.4, v = 40 + Math.random() * 30; this.bit(x, y - 10, Math.cos(a) * v, Math.sin(a) * v * 0.7 - 10, t, SF.popSec, i % 2 ? CYAN : WHITE); }
  }
  private bit(x: number, y: number, vx: number, vy: number, t0: number, life: number, col: number) { this.bits.put({ x, y, vx, vy, t0, life, col }); }

  /** Draw this frame. `comfort` is the comfort grade's scale (1 full .. 0.45 low); `wu`/`boss` place the live telegraph. */
  draw(sg: Sg | undefined, npcs: NpcSnap[], t: number, comfort: number, lowFx: boolean) {
    const g = this.ground, o = this.over; g.clear(); o.clear();
    const boss = sg && npcs.find(n => n.id === sg.id);
    // the wind-up telegraph: always shown (it decides whether you live). A warning-colored wedge of his exact reach and
    // arc fills from his feet outward over the wind-up; the edge brightens as the sweep comes.
    if (sg?.wu && boss && this.wind) {
      const w = telegraphWedge(boss.x, boss.y, this.wind.a), f = Math.min(1, (t - this.wind.t0) / Math.max(0.05, B().melee.windup));
      const wedge = (r: number) => g.moveTo(w.x, w.y).lineTo(w.x + Math.cos(w.a0) * r, w.y + Math.sin(w.a0) * r).arc(w.x, w.y, r, w.a0, w.a1).closePath();
      wedge(w.r).fill({ color: WARN, alpha: 0.14 + 0.08 * comfort });
      wedge(Math.max(4, w.r * f)).fill({ color: WARN, alpha: 0.2 + 0.16 * comfort });
      g.moveTo(w.x + Math.cos(w.a0) * w.r, w.y + Math.sin(w.a0) * w.r).arc(w.x, w.y, w.r, w.a0, w.a1).stroke({ color: WARN, width: 2, alpha: 0.6 + 0.4 * f });
      for (const a of [w.a0, w.a1]) g.moveTo(w.x, w.y).lineTo(w.x + Math.cos(a) * w.r, w.y + Math.sin(a) * w.r).stroke({ color: WARN, width: 1, alpha: 0.6 });
      g.moveTo(w.x + Math.cos(w.a0) * (w.r + 1.5), w.y + Math.sin(w.a0) * (w.r + 1.5)).arc(w.x, w.y, w.r + 1.5, w.a0, w.a1).stroke({ color: INK, width: 1, alpha: 0.5 }); // dark rim: reads on pale gravel
    }
    // the sweep: a pale crescent across the full arc that thins and fades
    for (const s of this.sweeps.items) {
      const f = (t - s.t0) / SF.sweepSec; if (f < 0 || f > 1) continue;
      const M = B().melee, a0 = s.a - M.arc / 2, a1 = a0 + M.arc * Math.min(1, 0.4 + f * 3), fade = 1 - f;
      // the cut area flashes pale for a moment (the same wedge the telegraph warned), then the crescent trails off
      if (f < 0.5) g.moveTo(s.x, s.y).lineTo(s.x + Math.cos(a0) * M.reach, s.y + Math.sin(a0) * M.reach).arc(s.x, s.y, M.reach, a0, a1).closePath().fill({ color: WHITE, alpha: (0.5 - f) * 0.4 * comfort });
      o.moveTo(s.x + Math.cos(a0) * M.reach, s.y + Math.sin(a0) * M.reach).arc(s.x, s.y, M.reach, a0, a1).stroke({ color: WHITE, width: 3 * fade + 1, alpha: fade * (0.5 + 0.5 * comfort) });
      o.moveTo(s.x + Math.cos(a0) * (M.reach - 4), s.y + Math.sin(a0) * (M.reach - 4)).arc(s.x, s.y, M.reach - 4, a0, a1).stroke({ color: CYAN, width: 1.5, alpha: fade * 0.8 * comfort });
      if (SHINGAN_FX_REVIEW && !lowFx) o.moveTo(s.x + Math.cos(a0) * (M.reach + 4), s.y + Math.sin(a0) * (M.reach + 4)).arc(s.x, s.y, M.reach + 4, a0, a1).stroke({ color: WHITE, width: 1, alpha: fade * 0.4 * comfort });
    }
    for (const r of this.rings.items) {
      const f = (t - r.t0) / r.dur; if (f < 0 || f > 1) continue;
      const R = r.r * (1 - (1 - f) ** 3);
      g.circle(r.x, r.y, R).stroke({ color: r.col, width: r.w, alpha: (1 - f) * (0.45 + 0.55 * comfort) });
      if (r.col === WHITE && r.w > 2) g.circle(r.x, r.y, R).stroke({ color: DANGER, width: 1, alpha: (1 - f) * 0.6 }); // the shockwave's hurting edge
    }
    for (const k of this.streaks.items) { // the blink's path: a pale line that fades from where he left
      const f = (t - k.t0) / SF.ghostSec; if (f < 0 || f > 1) continue;
      const mx = k.x + (k.x1 - k.x) * f, my = k.y + (k.y1 - k.y) * f;
      o.moveTo(mx, my - 12).lineTo(k.x1, k.y1 - 12).stroke({ color: WHITE, width: 2, alpha: (1 - f) * (0.4 + 0.4 * comfort) });
      o.moveTo(mx, my - 12).lineTo(k.x1, k.y1 - 12).stroke({ color: CYAN, width: 1, alpha: (1 - f) * 0.8 });
    }
    for (const gh of this.ghosts) {
      const f = (t - gh.t0) / SF.ghostSec;
      gh.s.visible = f >= 0 && f < 1; if (gh.s.visible) gh.s.alpha = (1 - f) * 0.6 * Math.max(0.6, comfort);
    }
    if (this.arrive) { // a pale column settles onto him and a ring opens at his feet
      const f = (t - this.arrive.t0) / SF.arriveSec;
      if (f > 1) this.arrive = null;
      else { const { x, y } = this.arrive, h = 60 * (1 - f), wdt = 10 * (1 - f * 0.6); o.rect(x - wdt / 2, y - h, wdt, h).fill({ color: WHITE, alpha: (1 - f) * 0.35 * comfort }); o.rect(x - 1, y - h, 2, h).fill({ color: WHITE, alpha: (1 - f) * 0.7 }); }
    }
    for (const p of this.pops.items) { // the decoy breaks into pixel shards and a quick square of light
      const f = (t - p.t0) / SF.popSec; if (f < 0 || f > 1) continue;
      // the hologram collapses: its body box squeezes to a bright line, then a widening square of light breaks away
      const r = 4 + 12 * f; o.rect(p.x - r, p.y - 10 - r, r * 2, r * 2).stroke({ color: CYAN, width: 1.5, alpha: (1 - f) * (0.5 + 0.5 * comfort) });
      if (f < 0.4) { const h = 24 * (1 - f / 0.4); o.rect(p.x - 6, p.y + 2 - h, 12, Math.max(1, h)).fill({ color: CYAN, alpha: 0.35 * comfort + 0.15 }); o.rect(p.x - 7, p.y + 1 - h, 14, 1).fill({ color: WHITE, alpha: 0.9 }); }
    }
    for (const b of this.bits.items) {
      const age = t - b.t0; if (age < 0 || age > b.life) continue;
      o.rect(Math.round(b.x + b.vx * age), Math.round(b.y + b.vy * age), 1.5, 1.5).fill({ color: b.col, alpha: 1 - age / b.life });
    }
  }
  /** a decoy's shimmer band across its body box (only while `decoyShimmer` is on) */
  band(x: number, top: number, h: number, f: number, comfort: number) {
    this.over.rect(x - 8, top + f * h, 16, 1).fill({ color: WHITE, alpha: 0.4 * comfort });
  }
}
